import React from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CommentsFormTypes, ReceiptFormTypes } from "models/formTypes";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  AccordionPanel,
  FileViewerContainer,
  GenericCommentsView,
  TitledContainerInner,
  EntityCardContent,
} from "components/common";
import {
  HighReceiptAmountFloatingMessage,
  ReceiptFooter,
  ReceiptTodoSelectionFormContainer,
  ReceiptUploadedBy,
  useReceiptSnoozeAction,
  useReceiptAttachments,
} from "components/receipt";
import { useReceiptViewInfo } from "components/receipt";
import { CommentsViewWithInputForm } from "components/common/comments";
import { useReceiptService, useFileUploadService, useGenericCommentService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";

const ReceiptTodoSentBack: React.FC<TodoCardProps> = (props) => {
  const genericCommentService = useGenericCommentService();
  const fileUploadService = useFileUploadService();
  const receiptService = useReceiptService();

  const receipt = props.item.receipt;

  const commentsAndFilesFormRef = React.useRef<FormHandles>(null);
  const receiptTodoSelectionFormRef = React.useRef<FormHandles>(null);

  const [loading, setLoading] = React.useState(false);
  const [updatedFormValues, setUpdatedFormValues] = React.useState<Partial<ReceiptFormTypes.Todo>>();

  const { attachments, onUploadedFilesChange } = useReceiptAttachments(
    receipt!,
    receiptTodoSelectionFormRef,
    FormFieldConstants.Receipt.Todo.FilesToUpload
  );

  const onViewInfoClick = useReceiptViewInfo(receipt!.id, receiptTodoSelectionFormRef);
  const snoozeAction = useReceiptSnoozeAction(receipt!.id, props.onActionSuccess);

  const submitReceiptForm = async (submitFunc?: (values: ReceiptFormTypes.TodoExtended) => Promise<any>) => {
    const [commentWithFiles, todoValues] = await Promise.all([submitCommentsForm(), submitTodoForm()]);

    if (todoValues && submitFunc) {
      const files = await fileUploadService.updateMissingAttachments(
        todoValues.filesToUpload ?? [],
        receiptService.getAttachmentData.bind(null, receipt!.id)
      );

      return submitFunc({
        ...todoValues,
        filesToUpload: files,
        isOtherPaymentMethod: updatedFormValues?.paymentMethodId === OTHER_PAYMENT_METHOD_ID,
      }).then(() => {
        if (commentWithFiles?.explanation && receipt)
          genericCommentService.addGenericComment(
            receipt.id,
            WintSuperkollAPIModel.GenericCommentType.Receipt,
            commentWithFiles?.explanation
          );
      });
    }

    return Promise.reject();
  };

  const submitTodoForm = async (): Promise<ReceiptFormTypes.Todo | undefined> => {
    if (receiptTodoSelectionFormRef.current) {
      return receiptTodoSelectionFormRef.current.submit();
    }
  };

  const submitCommentsForm = async (): Promise<CommentsFormTypes.CommentWithFile | undefined> => {
    if (commentsAndFilesFormRef.current) {
      return commentsAndFilesFormRef.current.submit();
    }
  };

  const reloadTodo = (onSuccess?: (receipt: WintSuperkollAPIModel.Receipt) => void, onError?: () => void) => {
    if (receipt) {
      setLoading(true);
      receiptService
        .get(receipt.id)
        .then((result) => {
          onSuccess && onSuccess(result);
        })
        .catch(() => {
          onError && onError();
        })
        .finally(() => {
          setLoading(false);
          props.onActionSuccess();
        });
    }
  };

  const onFormUpdate = (values: ReceiptFormTypes.Todo) =>
    setUpdatedFormValues({
      purchaseDate: values.purchaseDate,
      amount: values.amount,
      currency: values.currency,
      receiptCategory: values.receiptCategory,
      paymentMethodId: values.paymentMethodId,
    });

  const fileViewer = <FileViewerContainer key={receipt?.id} meta={attachments} loading={attachments === undefined} />;

  const sentBackDate = receipt?.sendBackData?.date ?? receipt?.dateTime;

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            updatedFormValues?.amount ?? receipt?.amount ?? 0,
            updatedFormValues?.currency ?? receipt?.currency
          )}
          descriptionHeadingTwo={updatedFormValues?.receiptCategory ?? receipt?.categoryName ?? ""}
          descriptionheadingThree={
            <ReceiptUploadedBy personId={receipt?.createdByPersonId} personName={receipt?.createdByPersonName} />
          }
          footerText={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.TodoCardSubTypeText_ReceiptSentBack)}: ${
            sentBackDate ? LocalizationHelperInstance.getFormattedDate(sentBackDate) : "-"
          }`}
          onViewAllClick={onViewInfoClick}
          quickActions={[snoozeAction]}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptSentBack)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptSentBackDescription)}
        >
          {receipt && (
            <CommentsViewWithInputForm
              parentId={receipt.id}
              ref={commentsAndFilesFormRef}
              commentType={WintSuperkollAPIModel.GenericCommentType.Receipt}
              attachmentsHiddenCondition="always"
              initialValues={{
                postWithoutPicture: true,
                markAsResolved: true,
              }}
              commentOrFileRequired={false}
            />
          )}
        </TitledContainerInner>
      }
      infoViewer={attachments?.length ? fileViewer : undefined}
      footer={
        receipt ? (
          <ReceiptFooter
            receipt={receipt}
            receiptFormSubmitHandler={submitReceiptForm}
            reloadReceipt={reloadTodo}
            markAsDone={props.onActionSuccess}
            hideSave
            disableSuccessMessages
          />
        ) : (
          <></>
        )
      }
      loading={loading}
    >
      <AccordionPanel
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptEdit)}
        defaultExpanded
      >
        {receipt && (
          <ReceiptTodoSelectionFormContainer
            ref={receiptTodoSelectionFormRef}
            receipt={receipt}
            fileViewer={fileViewer}
            showFullForm
            onUploadedFilesChangeHandler={(updatedValue: UploadedFile[]) => onUploadedFilesChange("", updatedValue)}
            onFormUpdate={onFormUpdate}
          />
        )}
      </AccordionPanel>
      <GenericCommentsView parentId={receipt!.id} type={WintSuperkollAPIModel.GenericCommentType.Receipt} collapsed />
      <HighReceiptAmountFloatingMessage
        amount={updatedFormValues?.amount ?? receipt?.amount}
        currency={updatedFormValues?.currency ?? receipt?.currency}
        receiptCategoryId={receipt?.receiptCategoryId}
        date={receipt?.dateTime ? new Date(receipt.dateTime) : undefined}
      />
    </EntityCardContent>
  );
};

export default ReceiptTodoSentBack;
